import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cn } from '@/shared/lib';
import type {
  TabsContentProps,
  TabsIndicatorProps,
  TabsListProps,
  TabsProps,
  TabsTriggerProps,
} from '@/shared/ui/tabs/types';
import { tabsStyles } from '@/shared/ui/tabs/styles';

/**
 * Root of the tabs surface.
 *
 * Roving tabindex and arrow-key navigation come from the primitive: it keeps
 * exactly one tab in the page tab order and moves selection with the arrow
 * keys appropriate to the orientation. Hand-rolling that is how tab strips end
 * up with every tab reachable by Tab.
 */
export function Tabs({
  children,
  className,
  orientation = 'horizontal',
  onValueChange,
  value,
  defaultValue,
}: TabsProps) {
  return (
    <TabsPrimitive.Root
      className={cn(tabsStyles.root, orientation === 'vertical' && tabsStyles.rootVertical, className)}
      orientation={orientation}
      onValueChange={onValueChange}
      value={value}
      defaultValue={defaultValue}
    >
      {children}
    </TabsPrimitive.Root>
  );
}

/** Container for the tabs. Carries `role="tablist"`. */
export function TabsList({ className, ...props }: TabsListProps) {
  return <TabsPrimitive.List className={cn(tabsStyles.list, className)} {...props} />;
}

/**
 * A single tab. Carries `role="tab"` and is wired to the panel sharing its
 * value; the primitive owns `aria-controls` and `aria-selected`.
 */
export function TabsTrigger({ className, type = 'button', value, ...props }: TabsTriggerProps) {
  return (
    <TabsPrimitive.Tab className={cn(tabsStyles.trigger, className)} type={type} value={value} {...props} />
  );
}

/** The panel belonging to the tab with the same value. Carries `role="tabpanel"`. */
export function TabsContent({ className, keepMounted, value, ...props }: TabsContentProps) {
  return (
    <TabsPrimitive.Panel
      className={cn(tabsStyles.panel, className)}
      value={value}
      keepMounted={keepMounted}
      {...props}
    />
  );
}

/** The sliding highlight behind the selected tab. */
export function TabsIndicator({ className, ...props }: TabsIndicatorProps) {
  return <TabsPrimitive.Indicator className={cn(tabsStyles.indicator, className)} {...props} />;
}